import { i18n } from 'core/i18n';
import React from 'react';
const {
    formatText,
    FormattedTextWithNamedVariables,
    extractNamedVariables,
    getNamedVariablesValues,
    getLanguage
} = i18n;
import { SAVINGS_ACCOUNT_TYPE } from 'constants/index';
import {
    SAVING_ACCOUNTS_FLOWS,
    BASE_IMG_URL,
    SAVINGS_ACCOUNT_ID,
    SAVINGS_ACCOUNT_IMG,
    SAVINGS_ACCOUNT_PASSPORT_IMG,
    SAVINGS_ACCOUNT_HIRING_FLOW_URL,
    SAVINGS_ACCOUNT_LANDING_OFFERS_URL,
    SAVINGS_CC_OFFERS_LANDING,
    SAVINGS_ACCOUNT_DROPDOWN_TITLE,
} from 'constants/eAccountLoan';
import {
    WELCOME_ACCOUNT_FLOW,
    ACCOUNTS_FLOWS_MAP,
    WELCOME_ACCOUNT_HIRING_FLOW_URL,
    WELCOME_ACCOUNT_TYPE,
    WELCOME_ACCOUNT_ID
} from 'constants/accounts';
import { WELCOME_ACCOUNT } from 'constants/flagBasedFeatures';

const ACCOUNTS_FLOWS = [
    ...SAVING_ACCOUNTS_FLOWS,
    WELCOME_ACCOUNT_FLOW
]

const offerOnlyHasFlowId = (offer) => {
    return Object.keys(offer.metadata).length === 0 && Object.keys(offer.offersValues).length === 0;
}

const ACCOUNT_TYPE_CONFIGS = {
    [WELCOME_ACCOUNT_TYPE]: {
        id: WELCOME_ACCOUNT_ID,
        link: WELCOME_ACCOUNT_HIRING_FLOW_URL,
        featureTogglingKey: WELCOME_ACCOUNT
    },
    [SAVINGS_ACCOUNT_TYPE]: {
        id: SAVINGS_ACCOUNT_ID,
        link: SAVINGS_ACCOUNT_HIRING_FLOW_URL,
        title: SAVINGS_ACCOUNT_DROPDOWN_TITLE,
        linkLandingOffers: SAVINGS_ACCOUNT_LANDING_OFFERS_URL,
        sidebarImgUrl: `${BASE_IMG_URL}${SAVINGS_ACCOUNT_IMG}`,
        sidebarImgUrlByCountry: {
            "de": `${BASE_IMG_URL}${SAVINGS_ACCOUNT_PASSPORT_IMG}`,
            "nl": `${BASE_IMG_URL}${SAVINGS_ACCOUNT_PASSPORT_IMG}`,
            "es": `${BASE_IMG_URL}${SAVINGS_ACCOUNT_IMG}`,
            "pt": `${BASE_IMG_URL}${SAVINGS_ACCOUNT_PASSPORT_IMG}`
        }
    }
}

const getNamedVariables = (key, offerData) => {
    const namedVariables = extractNamedVariables(key);
    const namedVariablesValues = getNamedVariablesValues(namedVariables, offerData);
    return namedVariablesValues;
}

const formatAdvantages = (offerData) => {
    const { accountTexts, accountConfig, context } = offerData;

    const formattedAdvantages = accountTexts.map(text => {
        const { conditionConfig, logo, keyName } = text;

        const advantageText = `${context}-${keyName}`;

        const namedVariablesContainer = {
            ...accountConfig,
            ...conditionConfig,
        };
        const namedVariablesValues = getNamedVariables(advantageText, namedVariablesContainer);

        return {
            text: <FormattedTextWithNamedVariables value={advantageText} namedVariables={namedVariablesValues} />,
            icon: formatText(`${context}-${logo}`)
        }
    });
    return formattedAdvantages;
}

const handleButtonClick = (productId, history) => {
    history.push(`${SAVINGS_ACCOUNT_HIRING_FLOW_URL}${productId}`);
}

const getOfferBanner = (offer) => {
    let processedOfferBanner;
    const type = ACCOUNTS_FLOWS_MAP[offer.flowId];
    const typeConfig = ACCOUNT_TYPE_CONFIGS[type];
    const redirectToLandingOffers = offer.flowId === SAVINGS_CC_OFFERS_LANDING
    if (offerOnlyHasFlowId(offer)) {
        return {
            [type]: {
                id: typeConfig.id,
                title: typeConfig.title,
                link: redirectToLandingOffers ? typeConfig.linkLandingOffers : `${typeConfig.link}${offer.productId}`,
                ...(typeConfig.featureTogglingKey && { featureTogglingKey: typeConfig.featureTogglingKey }),
            }
        }
    }
    const { metadata, productId } = offer;
    const { accountConfig, context } = metadata;
    const { name: title, subtitle } = accountConfig;

    const titleKey = `${context}-${title}`;
    const subtitleKey = `${context}-${subtitle}`;

    if (__CONTACT_CENTER__) {
        processedOfferBanner = {
            [type]: {
                id: typeConfig.id,
                title: typeConfig.title || titleKey,
                link: redirectToLandingOffers ? typeConfig.linkLandingOffers : `${typeConfig.link}${productId}`,
                ...(typeConfig.featureTogglingKey && { featureTogglingKey: typeConfig.featureTogglingKey }),
            }
        };
    } else {
        const formattedAdvantages = formatAdvantages(metadata);
        processedOfferBanner = {
            [type]: {
                id: typeConfig.id,
                title: titleKey,
                subtitle: subtitleKey,
                sidebarImgAlt: titleKey,
                sidebarImgUrl: typeConfig.sidebarImgUrl,
                sidebarImgUrlByCountry: typeConfig.sidebarImgUrlByCountry,
                advantages: formattedAdvantages,
                onButtonClick: (history) => handleButtonClick(productId, history),
                textsAlreadyTranslated: true,
            }
        };
    }
    return processedOfferBanner;
}

const getRepresentativeExamples = (offer) => {
    const { accountConfig, accountTexts, context } = offer.metadata;

    const representativeExamples = accountTexts.reduce((acc, accountText) => {
        const { representativeExampleKey, conditionConfig } = accountText;

        const exampleText = `${context}-${representativeExampleKey}`;

        if (!representativeExampleKey) {
            return acc;
        }

        const namedVariablesContainer = {
            ...accountConfig,
            ...conditionConfig,
        }
        const namedVariablesValues = getNamedVariables(exampleText, namedVariablesContainer);
        const representativeExample = <FormattedTextWithNamedVariables value={exampleText} namedVariables={namedVariablesValues} />;

        acc.push(representativeExample);

        return acc;
    }, []);

    return representativeExamples;
}

const getErrorBanner = (fetchOffers) => (
    {
        offerBanner: {
            [SAVINGS_ACCOUNT_TYPE]: {
                isError: true,
                title: 'accounts-accounts_noDataBannerErrorTitle',
                subtitle: 'accounts-accounts_noDataBannerErrorSubtitle',
                onButtonClick: () => fetchOffers({
                    flow: 'ACCOUNTS_LANDING',
                    metadata: {
                        language: getLanguage().toUpperCase()
                    }
                }),
                buttonText: 'common-retry',
                errorTertiaryText: 'accounts-accounts_noDataBannerErrorMessage'
            }
        },
        representativeExamples: [],
    }
)

const validateOfferData = (offers) => {
    if (!Array.isArray(offers.offers)) {
        return null
    }
    return offers.offers.every(offer => ACCOUNTS_FLOWS.includes(offer.flowId));
}

const processOffer = (offer) => {
    let representativeExamples = [];
    if (SAVING_ACCOUNTS_FLOWS.includes(offer.flowId) && !Object.keys(offer.metadata).length === 0) {
        representativeExamples = getRepresentativeExamples(offer);
    }
    const offerBanner = getOfferBanner(offer);
    return {
        offerBanner,
        representativeExamples,
    }
}

const processOffers = (offers) => {
    return offers.reduce((acc, offer) => {
        const { offerBanner, representativeExamples } = processOffer(offer);
        acc.offerBanner = {
            ...acc.offerBanner,
            ...offerBanner
        };
        if (representativeExamples.length > 0) {
            acc.representativeExamples = [...acc.representativeExamples, ...representativeExamples];
        }
        return acc;
    }, {
        offerBanner: {},
        representativeExamples: [],
    });
}

export const offersAccountsAdapter = (offersData = {}, fetchOffers) => {
    const { data: offers, error, fetched } = offersData;
    if (error) {
        return getErrorBanner(fetchOffers);
    }
    if (Object.keys(offersData).length === 0 || !offers || !fetched || !validateOfferData(offers)) {
        return {
            offerBanner: {},
            representativeExamples: [],
        };
    }

    const filteredOffers = offers.offers.filter((offer, index, self) =>
        self.findIndex(t => t.flowId === offer.flowId) === index
    );

    return processOffers(filteredOffers);

}
